// frontend/src/pages/BatchOptimizationPage.tsx

import React from 'react';
import { Box } from '@mui/material';
import { useBackTarget } from '../modules/pricingJourney/navigation';
import PageHeader from '../modules/pricingJourney/components/PageHeader';
import BatchOptimization from '../components/BatchOptimization';
import { useAuth } from '../context/AuthContext';
import FeatureLockedState from '../components/Common/FeatureLockedState';
import { TrialLimitNotice, trialUsage } from '../modules/pricingJourney/components/TrialBanner';
import PageSkeleton from '../modules/pricingJourney/components/Skeletons';

const BatchOptimizationPage: React.FC = () => {
  const back = useBackTarget('/optimization-center', 'Volver a Optimización');
  const { user, isLoading, pricingAccess } = useAuth();
  const trialUse = trialUsage(pricingAccess);
  const trialLimitReached = pricingAccess?.state === 'trial' && trialUse.used >= trialUse.limit;

  // --- 1. ESTADO DE CARGA BLINDADO ---
  // Si está cargando auth O BIEN tenemos usuario pero faltan sus detalles de tier (aún bajando de la DB)
  // mostramos el spinner. Esto elimina el parpadeo.
  const isProfileLoading = user && !user.tier_details;

  // PRC-031: estructura al instante en lugar de una rueda.
  if (isLoading || isProfileLoading) {
    return <PageSkeleton eyebrow="Optimización" title="Optimización por lotes" variant="table" />;
  }

  // --- 2. LÓGICA DE PERMISO ---
  // Ahora es seguro leer esto porque sabemos que tier_details ya llegó
  const canAccessBatch = user?.tier_details?.feature_batch_optimization === true;

  // PRC-032: cabecera común; «Volver» al origen o a Optimización.
  const backButton = (
    <PageHeader
      eyebrow="Optimización"
      title="Optimización por lotes"
      description="Sube un archivo con tus productos, indica qué columna es cada dato y descarga los precios recomendados."
      back={{ label: back.label, onClick: back.go }}
    />
  );

  // ESCENARIO 3: ACCESO DENEGADO
  if (!canAccessBatch) {
    return (
      <Box
        sx={{
          width: '100%',
          maxWidth: 'none',
          px: { xs: 2, md: 3, xl: 5 },
          pt: 2.5,
          pb: 6,
        }}
      >
        {backButton}

        <Box
          sx={{
            minHeight: '60vh',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <FeatureLockedState
            title="Optimización por lotes"
            featureName="Optimización por lotes"
            description="Analiza y optimiza cientos de productos simultáneamente subiendo tus archivos Excel o CSV."
            features={[
              'Procesamiento masivo de datos',
              'Mapeo inteligente de columnas',
              'Resultados descargables en Excel',
              'Historial de ejecuciones'
            ]}
          />
        </Box>
      </Box>
    );
  }

  // ESCENARIO 4: ACCESO PERMITIDO
  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: 'none',
        px: { xs: 2, md: 3, xl: 5 },
        pt: 2.5,
        pb: 6,
      }}
    >
      {backButton}

      {/* PRC-024: aviso B4 con las optimizaciones de la prueba agotadas. */}
      {trialLimitReached && <Box sx={{ mb: 3 }}><TrialLimitNotice /></Box>}

      <Box
        sx={{
          width: '100%',
          position: 'relative',
        }}
      >
        <BatchOptimization />
      </Box>
    </Box>
  );
};

export default BatchOptimizationPage;